import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ImageCaption from './ImageCaption';
import MediaFrame from './MediaFrame';
import HeldPicture from './HeldPicture';
import { FADE_DURATION } from '../imageViewerUtils';
import {
    DEFAULT_SPREAD_SETTINGS,
    getPlacement,
    getPlacedImageStyle,
} from '../../spreadLayoutUtils';

/**
 * Reusable image slide component
 * Used for current image, preloaded images, and fade overlay
 * 
 * When pageDimensions is provided, renders as a panoramic spread (image spanning two pages)
 * Supports scale and anchor positioning within the spread.
 * 
 * @param {Object} props
 * @param {Object} props.imageData - Image data object with file, dimensions, scale, anchor
 * @param {string} props.alt - Alt text for the image
 * @param {boolean} props.priority - Whether to prioritize loading this image
 * @param {boolean} props.hidden - Whether the image is hidden (preload only)
 * @param {number} props.opacity - Custom opacity for fade transitions
 * @param {boolean} props.isOverlay - Whether this is a fade overlay (pointer-events-none)
 * @param {number} props.zIndex - z-index for layering
 * @param {boolean} props.isMobile - Whether in mobile layout (different padding)
 * @param {Object} props.style - Additional inline styles
 * @param {Object} props.pageDimensions - Unified page dimensions for panoramic spread display
 * @param {number} props.gap - Gap between pages as percentage of page width
 */
const ImageSlide = ({ 
    imageData, 
    alt = '', 
    priority = false, 
    hidden = false,
    opacity,
    isOverlay = false,
    zIndex = 2,
    isMobile = false,
    removePadding = false,
    style = {},
    pageDimensions = null,
    gap = 0,
    description = '',
    imageId = '',
    showDescription = false,
    frame = false,
}) => {
    if (!imageData) return null;
    
    const containerStyle = {
        zIndex,
        opacity: hidden ? 0 : (opacity !== undefined ? opacity : 1),
        transition: opacity !== undefined ? `opacity ${FADE_DURATION}ms ease-in-out` : 'none',
        ...style,
    };
    
    const paddingClass = removePadding
        ? 'p-0'
        : isMobile 
        ? 'px-2 py-14' 
        : 'px-2 md:px-15 xl:px-12 py-14 md:py-15';
    
    // Get scale, anchor, and fill from imageData (for single images with settings)
    const scale = imageData.scale || DEFAULT_SPREAD_SETTINGS.scale;
    const fill = imageData.fill || DEFAULT_SPREAD_SETTINGS.fill;
    
    // Calculate spread dimensions when pageDimensions is provided
    const spreadLayout = useMemo(() => {
        if (!pageDimensions) return null;
        
        const pageAspectRatio = pageDimensions.aspectRatio || 2/3;
        const gapFraction = gap / 100;
        // Spread aspect ratio = 2 pages + gap
        const spreadAspectRatio = pageAspectRatio * (2 + gapFraction);
        
        return {
            pageAspectRatio,
            spreadAspectRatio,
            gapFraction,
        };
    }, [pageDimensions, gap]);
    
    const pictureFile = imageData.file || imageData;
    
    // Panoramic spread view (when pageDimensions is provided)
    if (spreadLayout) {
        // Pure CSS spread sizing using container query units
        // Width = min(available width, available height * aspect ratio)
        const baseContainerStyle = {
            aspectRatio: spreadLayout.spreadAspectRatio,
            width: `min(100cqw, calc(100cqh * ${spreadLayout.spreadAspectRatio}))`,
            height: 'auto',
            maxHeight: '100cqh',
        };
        
        const spreadContainerStyle = {
            ...baseContainerStyle,
            position: 'relative',
            overflow: 'hidden',
        };
        
        // Outer container style with container-type for cqw/cqh units
        const outerContainerStyle = {
            ...containerStyle,
            containerType: 'size',
        };
        
        const imageStyle = getPlacedImageStyle(scale, fill, getPlacement(imageData));
        
        return (
            <div 
                className={`absolute inset-0 flex flex-col items-center justify-center ${paddingClass} bg-background ${isOverlay ? 'pointer-events-none' : ''}`}
                style={outerContainerStyle}
            >
                {/* Spread container - pure CSS sizing with container queries */}
                <MediaFrame
                    enabled={frame}
                    className="overflow-hidden"
                    style={spreadContainerStyle}
                >
                    <HeldPicture
                        file={pictureFile}
                        alt={alt}
                        priority={priority}
                        style={imageStyle}
                    />
                </MediaFrame>
                {/* Description below image - positioned absolutely at bottom for smooth transitions */}
                {showDescription && (
                    <div 
                        className="absolute bottom-0 left-0 right-0 pb-4 text-center"
                        style={{
                            opacity: opacity !== undefined ? opacity : 1,
                            transition: opacity !== undefined ? `opacity ${FADE_DURATION}ms ease-in-out` : 'none',
                        }}
                    >
                        <ImageCaption description={description} imageId={imageId || imageData?.id} />
                    </div>
                )}
            </div>
        );
    }
    
    // Standard single image view (no pageDimensions - mobile or fallback)
    return (
        <div 
            className={`absolute inset-0 flex flex-col items-center justify-center ${paddingClass} bg-background ${isOverlay ? 'pointer-events-none' : ''}`}
            style={containerStyle}
        >
            {/* HeldPicture paints backgrounds and has no intrinsic size, so the frame must. */}
            <MediaFrame enabled={frame} className="relative flex h-full w-full min-h-0 min-w-0 items-center justify-center">
                <HeldPicture file={pictureFile} alt={alt} priority={priority} />
            </MediaFrame>
            {/* Description below image - positioned absolutely at bottom for smooth transitions */}
            {showDescription && (
                <div 
                    className="absolute bottom-0 left-0 right-0 pb-4 text-center"
                    style={{
                        opacity: opacity !== undefined ? opacity : 1,
                        transition: opacity !== undefined ? `opacity ${FADE_DURATION}ms ease-in-out` : 'none',
                    }}
                >
                    <ImageCaption description={description} imageId={imageId || imageData?.id} />
                </div>
            )}
        </div>
    );
};

ImageSlide.propTypes = {
    imageData: PropTypes.object,
    alt: PropTypes.string,
    priority: PropTypes.bool,
    hidden: PropTypes.bool,
    opacity: PropTypes.number,
    isOverlay: PropTypes.bool,
    zIndex: PropTypes.number,
    isMobile: PropTypes.bool,
    removePadding: PropTypes.bool,
    style: PropTypes.object,
    pageDimensions: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
        aspectRatio: PropTypes.number,
    }),
    gap: PropTypes.number,
    description: PropTypes.string,
    imageId: PropTypes.string,
    showDescription: PropTypes.bool,
    frame: PropTypes.bool,
};

export default ImageSlide;
